import { useEffect } from "react";

import { deepActiveElement, isEditableTarget } from "@tsmono/util";

interface ArrowStepperOptions {
  /** Invoked on ArrowLeft when `canPrev` is set. */
  onPrev?: () => void;
  /** Invoked on ArrowRight when `canNext` is set. */
  onNext?: () => void;
  /** Whether stepping to the previous item is currently available. */
  canPrev: boolean;
  /** Whether stepping to the next item is currently available. */
  canNext: boolean;
}

/**
 * Global ArrowLeft/ArrowRight prev/next stepping for a "one of a sequence"
 * surface (e.g. the previous/next sample or result chevrons).
 *
 * Binds a capture-phase `keydown` listener on `document` so the shortcut works
 * regardless of focus, while standing down when the user is typing (focus in an
 * editable element, shadow-DOM-pierced) or holding any modifier (Meta/Ctrl/
 * Shift/Alt) — Alt+Arrow is the browser's Back/Forward, and the other chords
 * belong to the browser/OS. A direction only fires (and only then calls
 * `preventDefault`) when it is available and its callback is wired; otherwise
 * the key falls through to the browser untouched.
 *
 * This is the keyboard binding only — Enter/Space activation of focused
 * controls and all rendering stay with the consumer.
 */
export function useArrowStepper({
  onPrev,
  onNext,
  canPrev,
  canNext,
}: ArrowStepperOptions): void {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Leave modified arrows to the browser (Alt+← is Back, Ctrl/Meta/Shift)
      // and stand down while the user is typing.
      if (
        isEditableTarget(deepActiveElement()) ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        if (canPrev && onPrev) {
          e.preventDefault();
          onPrev();
        }
      } else if (e.key === "ArrowRight") {
        if (canNext && onNext) {
          e.preventDefault();
          onNext();
        }
      }
    };

    // Use capture phase to catch event before it reaches other handlers.
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [canPrev, canNext, onPrev, onNext]);
}
